import React, { createContext, useContext, useState, useEffect } from 'react';

interface SchemeContextProps {
  schemeTypeContext: string;
  schemeIdContext: number;
  updateSchemeType: (newSchemeType: string) => void;
  updateSchemeModule: (newSchemeModule: string[]) => void;
  updateSchemeModuleOptions: (newSchemeModuleOptions: string[]) => void;
  updateSchemeId: (newSchemeId: number) => void;
  updateSchemeAdminSchemeFilters: (newSchemeAdminFilters: string[]) => void;
  updateReadWritePermissions: (readWrite: boolean) => void;
  updateUserStatus: (newStat: string) => void;
  schemeModuleOptionCon: string[];
  schemeModuleCon: string[];
  schemeAdminSchemeFilters: string[];
  isReadWrite: boolean;
  globalUserStatus: string;
}

const SchemeContext = createContext<SchemeContextProps | undefined>(undefined);

export const useSchemeContext = () => {
  const context = useContext(SchemeContext);
  if (!context) {
    throw new Error('useSchemeContext must be used within a SchemeContextProvider');
  }
  return context;
};

interface SchemeContextProviderProps {
  children: React.ReactNode;
}

const SchemeContextProvider: React.FC<SchemeContextProviderProps> = ({ children }) => {
  const [schemeIdContext, setSchemeIdContext] = useState<number>(
    JSON.parse(localStorage?.getItem('schemeIdContext') || '0')
  );

  const [schemeTypeContext, setSchemeTypeContext] = useState<string>(
    localStorage?.getItem('schemeTypeContext') || ''
  );

  const [schemeModuleCon, setSchemeModuleCon] = useState<string[]>(
    JSON.parse(localStorage?.getItem('schemeModuleCon') || '[]')
  );

  const [schemeModuleOptionCon, setSchemeModuleOptionCon] = useState<string[]>(
    JSON.parse(localStorage?.getItem('schemeModuleOptionCon') || '[]')
  );

  const getLocalStorageData = (key: string) => {
    try {
      const storedValue = localStorage?.getItem(key);
      return storedValue ? JSON.parse(storedValue) : [];
    } catch (error) {
      console.error(`Error parsing localStorage for ${key}:`, error);
      return [];
    }
  };
  
  const [schemeAdminSchemeFilters, setSchemeAdminSchemeFilters] = useState<string[]>(
    getLocalStorageData('schemeAdminSchemeFilters')
  );

  const [globalUserStatus, setGlobalUserStatus] = useState<string>(
    localStorage?.getItem('globalUserStatus') || ''
  );

  const [isReadWrite, setIsReadWrite] = useState<boolean>(
    JSON.parse(localStorage?.getItem('isReadWrite') || 'false')
  );

  useEffect(() => {
    localStorage?.setItem('schemeIdContext', JSON.stringify(schemeIdContext));
  }, [schemeIdContext]);

  useEffect(() => {
    localStorage?.setItem('schemeTypeContext', schemeTypeContext);
  }, [schemeTypeContext]);

  useEffect(() => {
    localStorage?.setItem('schemeModuleCon', JSON.stringify(schemeModuleCon));
  }, [schemeModuleCon]);

  useEffect(() => {
    localStorage?.setItem('schemeModuleOptionCon', JSON.stringify(schemeModuleOptionCon));
  }, [schemeModuleOptionCon]);

  useEffect(() => {
    localStorage?.setItem('schemeAdminSchemeFilters', JSON.stringify(schemeAdminSchemeFilters));
  }, [schemeAdminSchemeFilters]);

  useEffect(() => {
    localStorage?.setItem('globalUserStatus', globalUserStatus);
  }, [globalUserStatus]);

  useEffect(() => {
    localStorage?.setItem('isReadWrite', JSON.stringify(isReadWrite));
  }, [isReadWrite]);

  const updateSchemeId = (newSchemeId: number) => {
    setSchemeIdContext(newSchemeId);
  };

  const updateSchemeType = (newSchemeType: string) => {
    setSchemeTypeContext(newSchemeType);
  };

  const updateSchemeModule = (newSchemeModule: string[]) => {
    setSchemeModuleCon(newSchemeModule);
  };

  const updateSchemeModuleOptions = (newSchemeModuleOptions: string[]) => {
    setSchemeModuleOptionCon(newSchemeModuleOptions);
  };

  const updateSchemeAdminSchemeFilters = (newSchemeAdminFilters: string[]) => {
    setSchemeAdminSchemeFilters(newSchemeAdminFilters);
  };

  const updateUserStatus = (newStat: string) => {
    setGlobalUserStatus(newStat);
  };

  const updateReadWritePermissions = (readWrite: boolean) => {
    setIsReadWrite(readWrite);
  };

  const contextValue: SchemeContextProps = {
    schemeTypeContext,
    schemeModuleCon,
    schemeModuleOptionCon,
    schemeIdContext,
    schemeAdminSchemeFilters,
    isReadWrite,
    globalUserStatus,
    updateSchemeType,
    updateSchemeModule,
    updateSchemeModuleOptions,
    updateSchemeId,
    updateSchemeAdminSchemeFilters,
    updateReadWritePermissions,
    updateUserStatus,
  };

  return <SchemeContext.Provider value={contextValue}>{children}</SchemeContext.Provider>;
};

export default SchemeContextProvider;
